/**
 * IGRA-387 — опрос судьбы письма с кодом входа.
 *
 * Человек на igra.now входит только по коду из письма. Если почтовый сервер
 * письмо отклонил (Яндекс и Mail.ru так делают с новыми отправителями), ждать
 * бессмысленно — экран обязан сам это заметить и предложить другой канал.
 *
 * Правила опроса:
 *  - раз в 3 секунды и не дольше 60 секунд: дальше опрос уже ничего не решает,
 *    а вкладку человек может держать открытой часами;
 *  - финальный статус (`delivered`/`rejected`) останавливает таймер;
 *  - ошибка ручки (429, 500) — это наша проблема, а не человека: пишем в
 *    консоль, ждём дольше и после второй подряд сдаёмся, оставив экран как был;
 *  - поздний ответ уже остановленного опроса игнорируется: иначе закрытая
 *    модалка могла бы перерисоваться.
 */
import { getCurrentScope, onScopeDispose, ref } from 'vue'
import {
  ProfileApi,
  type OtpDeliveryChannel,
  type OtpDeliveryStatus,
  type OtpDeliveryStatusResponse,
} from '@/modules/profile/api/profileApi'
import { trackEvent } from '@/shared/analytics'

/** Шаг опроса. */
export const OTP_POLL_INTERVAL_MS = 3000
/** Предел опроса: дольше минуты держать таймер незачем. */
export const OTP_POLL_MAX_MS = 60000
/** Столько подряд неудачных запросов — и опрос прекращается. */
const MAX_FAILURES = 2

// `confirmed` тоже финальный: вход уже подтверждён другим каналом и
// судьба письма больше никого не интересует.
const FINAL_STATUSES: OtpDeliveryStatus[] = ['delivered', 'rejected', 'confirmed']

/**
 * IGRA-388/389 — ожидание подтверждения другим каналом.
 *
 * Здесь `rejected` финальным НЕ является: письмо уже отклонено и таким и
 * останется, а ждём мы ровно одного — подтверждения из бота или по звонку.
 */
const CONFIRM_STATUSES: OtpDeliveryStatus[] = ['confirmed']

/** Столько ждём подтверждения — ровно срок жизни ссылки на бота. */
export const OTP_CONFIRM_MAX_MS = 600000

export interface UseOtpDeliveryStatusOptions {
  /** Подмена запроса — нужна тестам. */
  fetch?: (email: string) => Promise<OtpDeliveryStatusResponse>
}

export function useOtpDeliveryStatus(options: UseOtpDeliveryStatusOptions = {}) {
  const fetchStatus = options.fetch ?? ((email: string) => ProfileApi.getLoginCodeStatus(email))

  const status = ref<OtpDeliveryStatus>('pending')
  const reason = ref<string | null>(null)
  /** IGRA-388/389 — одноразовый ключ на сессию, если вход подтвердили каналом. */
  const loginToken = ref<string | null>(null)
  const channels = ref<OtpDeliveryChannel[]>([])
  const isPolling = ref(false)

  let timer: ReturnType<typeof setTimeout> | null = null
  let email = ''
  let deadline = 0
  let failures = 0
  let trackedStatus: OtpDeliveryStatus | '' = ''
  let finalStatuses = FINAL_STATUSES
  /** Номер запуска: ответы прошлых запусков применять нельзя. */
  let generation = 0

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  const stop = () => {
    generation += 1
    clearTimer()
    isPolling.value = false
  }

  const reset = () => {
    stop()
    status.value = 'pending'
    reason.value = null
    loginToken.value = null
    channels.value = []
    trackedStatus = ''
  }

  const schedule = (delay: number) => {
    clearTimer()
    const mine = generation
    timer = setTimeout(() => {
      void poll(mine)
    }, delay)
  }

  const apply = (result: OtpDeliveryStatusResponse) => {
    status.value = result.status
    reason.value = result.reason ?? null
    loginToken.value = result.login_token ?? null
    if (Array.isArray(result.channels)) {
      channels.value = result.channels
    }

    if (trackedStatus !== result.status) {
      trackedStatus = result.status
      trackEvent('otp_delivery_status', { status: result.status })
    }
  }

  const poll = async (mine: number) => {
    timer = null
    if (!isPolling.value || mine !== generation) return

    try {
      const result = await fetchStatus(email)
      if (!isPolling.value || mine !== generation) return

      failures = 0
      apply(result)

      if (finalStatuses.includes(result.status)) {
        stop()
        return
      }
    } catch (error) {
      if (!isPolling.value || mine !== generation) return

      failures += 1
      console.error('[igra] не удалось узнать статус письма с кодом', error)

      if (failures >= MAX_FAILURES) {
        stop()
        return
      }

      schedule(OTP_POLL_INTERVAL_MS * 2 ** failures)
      return
    }

    if (Date.now() >= deadline) {
      stop()
      return
    }

    schedule(OTP_POLL_INTERVAL_MS)
  }

  /** Начать опрос для адреса, на который только что ушёл код. */
  const start = (targetEmail: string) => {
    reset()
    email = targetEmail
    finalStatuses = FINAL_STATUSES
    deadline = Date.now() + OTP_POLL_MAX_MS
    failures = 0
    isPolling.value = true
    schedule(OTP_POLL_INTERVAL_MS)
  }

  /**
   * Человек ушёл в другой канал — ждём подтверждения.
   *
   * Состояние экрана не сбрасываем: письмо так и осталось отклонённым, и
   * блок с каналами обязан остаться на месте, пока человек в боте.
   */
  const awaitConfirmation = (targetEmail: string) => {
    stop()
    email = targetEmail
    finalStatuses = CONFIRM_STATUSES
    deadline = Date.now() + OTP_CONFIRM_MAX_MS
    failures = 0
    isPolling.value = true
    schedule(OTP_POLL_INTERVAL_MS)
  }

  if (getCurrentScope()) {
    onScopeDispose(stop)
  }

  return { status, reason, loginToken, channels, isPolling, start, awaitConfirmation, stop, reset }
}
